import React, { useState } from "react";
import type { FC, PropsWithChildren } from "react";
import { ModalHeading, Modal, Button, ModalForm } from "../../components/Modal";
import SelectInputBox from "../../components/Modal/SelectInputBox";
import classes from "../../styles/CustomStyles.module.css";
import {
  useActivatePatient,
  useInactivatePatient,
  useFetchPlan,
} from "../../hooks/patients";
import { IPatient, IntakeFormQuestion } from "../../hooks/patients/index.type";
import IntakeFormModal from "./ActivateIntakeForm";

type ActiveToggleModalProps = PropsWithChildren<{
  show: boolean;
  onClose?: () => void;
  onSave?: (event: React.FormEvent<HTMLFormElement>) => void;
  isActive?: boolean;
  patient: IPatient;
}>;
const ActiveToggleModal: FC<ActiveToggleModalProps> = ({
  show,
  onClose,
  onSave,
  patient,
  isActive = false,
}) => {
  const [carePlanTemplate, setCarePlanTemplate] = useState("");
  const [carePlanError, setCarePlanError] = useState("");
  const [showIntakeFormModal, setShowIntakeFormModal] = useState(false);
  const [intakeFormData, setIntakeFormData] =
    React.useState<IntakeFormQuestion[]>();

  const { inactivatedPatientLoading, inactivatePatient } =
    useInactivatePatient();
  const { activatePatient, activatedPatientLoading } = useActivatePatient();

  const { getPlans, planLoading, plans } = useFetchPlan();

  React.useEffect(() => {
    getPlans();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCarePlanError("");

    try {
      let response;
      if (!isActive) {
        if (!carePlanTemplate) {
          setCarePlanError("Please select a care plan template.");
          return;
        }

        response = await activatePatient({
          patientId: patient?._id ?? "",
          planId: carePlanTemplate,
          intakeFormDetails: intakeFormData,
        });
      } else {
        response = await inactivatePatient({ patientId: patient?._id ?? "" });
      }

      if (response) {
        if (onSave) onSave(e);
        if (onClose) onClose();
      }
    } catch (error) {
      console.error("Error during activate/inactivate:", error);
    }
  };

  const handleIntakeFormClick = () => {
    setShowIntakeFormModal(true);
  };

  const handleIntakeFormClose = () => {
    setShowIntakeFormModal(false);
  };

  const handleIntakeFormData = (data: IntakeFormQuestion[]) => {
    setIntakeFormData(data);
  };

  return (
    <>
      <Modal
        show={show}
        modalClassName={`${classes.container} ${classes.container_50rem}`}
      >
        <ModalHeading
          heading={isActive ? "Inactivate Patient" : "Activate Patient"}
          customClass={classes.header}
          customCloseClass={classes.close_btn}
          closeIcon="close-cross"
          onClose={onClose}
          subHeading={`Patient Name : ${patient?.name || "-"}`}
        />

        <ModalForm onSubmit={handleSubmit}>
          <div
            className={`displayflex displayflexCol gap-2 ${classes.modal_container} mt-2 mb-2`}
          >
            {!isActive ? (
              <>
                <SelectInputBox
                  label="Care Plan Template"
                  value={carePlanTemplate}
                  customClass={classes.select_box}
                  inputContainerStyle={{ maxWidth: "100%" }}
                  onChange={(e) => {
                    setCarePlanTemplate(e.target.value);
                    setCarePlanError("");
                  }}
                  errorMessage={carePlanError}
                >
                  <option value="">Select a care plan template</option>
                  {planLoading ? (
                    <option disabled>Loading care plan template...</option>
                  ) : (
                    plans?.map((plan) => (
                      <option key={plan._id} value={plan._id}>
                        {plan?.name}
                      </option>
                    ))
                  )}
                </SelectInputBox>
                {/* <Button
                  label="Intake Form"
                  span={false}
                  customClass={classes.intake_btn}
                  onClick={handleIntakeFormClick} 
                  type="button"
                  disabled={!carePlanTemplate}
                  icon="icon-clipboard"
             
                /> */}
              </>
            ) : (
              <div
                className={`displayflex gap-1 alignStart ${classes.error_container}`}
              >
                <div className="displayflex alignCenter justifyCenter mt-half">
                  <svg>
                    <use xlinkHref={`/assets/sprite.svg#icon-info-2`} />
                  </svg>
                </div>
                <p>
                  Are you sure you want to deactivate this patient? This will
                  stop their billing cycle.
                </p>
              </div>
            )}

            <Button
              label={isActive ? "Inactivate Patient" : "Activate Patient"}
              span={false}
              icon={isActive ? "icon-inactive-patient" : "icon-active-patient"}
              customClass={classes.btn}
              variant={isActive ? "danger" : "success"}
              iconStyle={{ width: "2rem", height: "1.6rem" }}
              type="submit"
              disabled={activatedPatientLoading || inactivatedPatientLoading}
            />
          </div>
        </ModalForm>
      </Modal>

      {showIntakeFormModal && (
        <IntakeFormModal
          showModal={showIntakeFormModal}
          onCloseModal={handleIntakeFormClose}
          planId={carePlanTemplate}
          handleIntakeFormData={handleIntakeFormData}
        />
      )}
    </>
  );
};
export default ActiveToggleModal;
